import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { MUICheckCard } from 'components/common/MUICards'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import { GRAY_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { formatPrice } from 'utils/price/priceFormatter'
import type { CurrencyCode, ResolvedVariant, SalesforceId } from 'models/purchaseFlowRevamp'

import { isQuotaFree } from '../../store/quotaPreview'
import { StruckPrice } from '../StruckPrice.component'
import { ClientPrice } from '../ClientPrice.component'

/** Variant cards drop the ".00" of a whole amount ("€199", Figma C9); the rest of the app keeps two decimals. */
const formatVariantPrice = (unitPrice: string, currency: CurrencyCode): string =>
  formatPrice(unitPrice, currency, undefined, { hideZeroCents: true })

const QuotaFreePrice: React.FC<{ variant: ResolvedVariant }> = ({ variant: { price, basePrice } }) => {
  if (!price || !basePrice) return null
  return (
    <StruckPrice
      price={formatVariantPrice(price.unitPrice, price.currency)}
      base={formatVariantPrice(basePrice.unitPrice, basePrice.currency)}
      variant="text-sm"
      free
    />
  )
}

interface Props {
  /** All variants of the shell product. */
  variants: ResolvedVariant[]
  /** Currently selected variant id (from the drawer slice). In `multiSelect`
   *  mode this is the primary pick; see `selectedVariantIds`. */
  selectedVariantId: SalesforceId | null
  /**
   * Every ticked variant, primary first. Only read in `multiSelect` mode;
   * elsewhere `selectedVariantId` alone decides.
   */
  selectedVariantIds?: readonly SalesforceId[]
  /**
   * Let the client tick SEVERAL variants instead of choosing one.
   *
   * Turned on for authorities-document groups, where each variant is a separate
   * document a client may legitimately want together (legacy rendered exactly
   * that: a check-card per document). Renders checkboxes with `role="checkbox"`
   * so assistive tech is told the choice is not exclusive.
   */
  multiSelect?: boolean
  /** Render as the authorities-document list. Separate from [multiSelect]:
   *  Bauakte looks like its siblings but admits only one pick. */
  documentStyle?: boolean
  /** Called when the user picks a variant, or ticks/unticks one in `multiSelect`. */
  onSelect: (variantId: SalesforceId) => void
  /** Optional label for the section header (e.g. "Number of photos",
   *  "Rendering type"). Falls back to the generic translation key. */
  sectionLabel?: string
  /**
   * Show ONLY the chosen variant, not switchable — what re-opening a line the
   * client already added should look like: the product is settled, and the drawer
   * is there to review or adjust what hangs off it (files, settings, extras).
   * Rendered as a plain tile, not a radio, so assistive tech is told there is no
   * choice here rather than being offered one that does nothing.
   */
  locked?: boolean
}

/**
 * Card-style variant picker (Figma 2733:21823 side panel).
 *
 * Each variant is a bordered card with radio + title + subtitle + price.
 * Selected state: tinted blue background + blue border. Unpriced variants
 * stay visible but disabled with a "not available" helper.
 */
export const VariantPicker: React.FC<Props> = ({
  variants,
  selectedVariantId,
  selectedVariantIds,
  multiSelect = false,
  documentStyle = false,
  onSelect,
  sectionLabel,
  locked = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (variants.length === 0) return null

  const tickedIds = selectedVariantIds ?? (selectedVariantId ? [selectedVariantId] : [])
  const isVariantSelected = (variantId: SalesforceId): boolean =>
    multiSelect ? tickedIds.includes(variantId) : variantId === selectedVariantId

  /**
   * Whether picking this variant excludes the others, and so whether it draws a
   * radio or a checkbox. A grouped document is exclusive with its siblings even
   * inside a multi-select group, because they resolve to the same document.
   *
   * Presentation only: the row still toggles off when clicked again, which is
   * the sole way to drop a grouped document once picked.
   */
  const isExclusive = (variant: ResolvedVariant): boolean =>
    !multiSelect || Boolean(variant.documentSelectionGroup)

  // A variant description has no home in a centred tile, and the wire does carry
  // the field (no dev response has ever populated it — checked against the
  // 2026-07-29 capture — but that can change server-side without a deploy).
  const hasDescriptions = variants.some((variant) => Boolean(variant.description))
  // The list is the only layout that shows a variant description, and a multi-select group must be the
  // list too: the tile grid draws no checkbox, so documents would look like a pick-one grid.
  const effectiveLayout = multiSelect || documentStyle || hasDescriptions ? 'list' : 'tile-grid'

  // Locked: only the chosen one. Falling back to the full list when the selection
  // cannot be found would quietly re-offer the choice, so an unresolvable
  // selection shows nothing rather than something switchable.
  const shownVariants = locked
    ? variants.filter((variant) => variant.id === selectedVariantId)
    : variants

  const priceOf = (variant: ResolvedVariant) => variant.priced && variant.price && (isQuotaFree(variant)
    ? <QuotaFreePrice variant={variant} />
    : <ClientPrice unitPrice={variant.price.unitPrice} currency={variant.price.currency} formatOptions={{ hideZeroCents: true }} variant="text-sm" />)

  const notAvailable = (variant: ResolvedVariant) => !variant.priced
    ? t('purchase_flow_revamp:drawer.variant_not_available')
    : undefined

  const isDisabled = (variant: ResolvedVariant) => !variant.active || !variant.priced

  /** A tile: name and price centred. */
  const tileLabel = (variant: ResolvedVariant) => (
    <Stack alignItems="center" gap={0.2} sx={{ lineHeight: '2rem' }}>
      <Typography variant="text-sm" fontWeight={550} color={isDisabled(variant) ? GRAY_500 : GRAY_900}>
        {variant.displayName}
      </Typography>
      {priceOf(variant) &&
        <Typography variant="text-sm" component="span" color={GRAY_700}>{priceOf(variant)}</Typography>
      }
      {notAvailable(variant) &&
        <Typography variant="text-xs" color={GRAY_500}>{notAvailable(variant)}</Typography>
      }
    </Stack>
  )

  return (
    <Stack gap={0.8} sx={{ width: '100%' }}>

      {/* The header is unconditional: a group with a single product still needs
          to say what the row below it is. Groups without their own label fall
          back to the generic one rather than rendering an unlabelled list. */}
      <Typography variant="text-md" fontWeight={450} color={GRAY_700}>
        {sectionLabel ?? t(multiSelect || documentStyle
          ? 'purchase_flow_revamp:drawer.variant_section_label_documents'
          : 'purchase_flow_revamp:drawer.variant_section_label_default')}
      </Typography>

      {effectiveLayout === 'tile-grid'
        ? (
          // A real 2-column grid, not rows of two: an odd last variant then
          // keeps its column width instead of stretching across the drawer.
          // One variant gets one column, so it does not sit as a lone half-tile —
          // which is also how a locked, single-tile picker renders.
          <SelectableButtonsGrid<SalesforceId>
            layout="vertical"
            selectionRole={locked ? undefined : 'radio'}
            readOnly={locked}
            gap={0.8}
            selectedValue={selectedVariantId}
            onSelect={onSelect}
            values={shownVariants.map((variant) => ({ value: variant.id, displayName: tileLabel(variant), disabled: isDisabled(variant) }))}
            // Two columns, so an odd last variant keeps its column width instead of stretching
            // across the drawer; a single variant takes the whole row.
            sx={{
              flex: shownVariants.length === 1 ? '1 1 100%' : '0 0 calc(50% - 0.4rem)',
              minHeight: '6.4rem',
              height: 'auto',
              padding: '1.2rem',
            }}
          />
        )
        : (
          <Stack gap={0.8} role={locked ? undefined : 'radiogroup'}>
            {shownVariants.map((variant) => (
              <MUICheckCard
                key={variant.id}
                isRadio={isExclusive(variant)}
                checked={isVariantSelected(variant.id)}
                disabled={isDisabled(variant)}
                onCheck={locked ? undefined : () => onSelect(variant.id)}
                title={variant.displayName}
                description={variant.description || notAvailable(variant)}
                trailing={priceOf(variant) &&
                  <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>{priceOf(variant)}</Typography>
                }
              />
            ))}
          </Stack>
        )
      }

    </Stack>
  )
}
